import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccess } from '@/access/useAccess';
import { useLayer } from '@/app/layers/useLayer';
import { useAuth } from '@/auth/AuthProvider';
import { resolveBillingState } from '@/billing/billingState';
import { useT } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import styles from './AvatarMenu.module.css';
import { LanguageSelect } from './LanguageSelect';
import { useTour } from '@/onboarding/TourProvider';
import { useShell } from './ShellContext';
import { ThemeSegment } from './ThemeSegment';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Кнопка-аватар: клик по ней не считается кликом снаружи. */
  anchorRef: React.RefObject<HTMLElement | null>;
}

/** Меню аватара 260px: имя/email (маска), план, Billing, Help, Theme, Language, Replay the tour, Log out. */
export function AvatarMenu({ open, onClose, anchorRef }: Props) {
  const { user, logout } = useAuth();
  const access = useAccess();
  const t = useT();
  const navigate = useNavigate();
  const { openHelp } = useShell();
  const { startTour } = useTour();
  const menuRef = useRef<HTMLDivElement>(null);
  // Слой стека: Esc закрывает только меню, а не панель разбора под ним; адрес не трогает.
  useLayer(open && !!user, onClose, { lockScroll: false, history: false });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, onClose, anchorRef]);

  if (!open || !user) return null;

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  // Метка плана: сначала беда с платной строкой (списание не прошло, пауза), потом доступ.
  const billing = resolveBillingState(user);
  const planTone = billing.paymentIssue
    ? 'pastDue'
    : billing.kind === 'paused'
      ? 'paused'
      : access.hasFullAccess
        ? 'active'
        : 'free';
  const planLabel =
    planTone === 'pastDue'
      ? t('menu.planPastDue')
      : planTone === 'paused'
        ? t('menu.planPaused')
        : planTone === 'active'
          ? t('menu.planActive')
          : t('menu.planFree');
  const planWarn = planTone === 'pastDue' || planTone === 'paused';

  return (
    <div ref={menuRef} className={styles.menu} role="menu" aria-label={t('nav.account')}>
      <div className={styles.head} data-clarity-mask="true">
        <div className={styles.name}>{user.name}</div>
        <div className={styles.email}>{user.email}</div>
        <span
          className={cx(
            styles.plan,
            planTone === 'active' && styles.planActive,
            planWarn && styles.planWarn,
          )}
        >
          {planLabel}
        </span>
      </div>

      <button type="button" role="menuitem" className={styles.item} onClick={() => go('/billing')}>
        {t('menu.billing')}
      </button>
      <button
        type="button"
        role="menuitem"
        className={styles.item}
        onClick={() => {
          onClose();
          openHelp('menu');
        }}
      >
        {t('menu.help')}
      </button>

      <div className={styles.row}>
        <span className={styles.rowLabel}>{t('menu.theme')}</span>
        <ThemeSegment />
      </div>
      <div className={cx(styles.row, styles.rowTight)}>
        <span className={styles.rowLabel}>{t('menu.language')}</span>
        <LanguageSelect />
      </div>

      <button
        type="button"
        role="menuitem"
        className={cx(styles.item, styles.itemMuted)}
        onClick={() => {
          onClose();
          startTour('menu');
        }}
      >
        {t('menu.replay')}
      </button>

      <div className={styles.divider} role="separator" />

      <button
        type="button"
        role="menuitem"
        className={cx(styles.item, styles.itemMuted)}
        onClick={() => {
          onClose();
          logout('manual');
        }}
      >
        {t('menu.logout')}
      </button>
    </div>
  );
}
